import {
  LitElement,
  html,
  css,
  styles,
  repeat,
  utils,
  unsafeHTML,
} from '@hh/global.js'
const { visuallyHidden } = styles
const { generateDividerMarkup } = utils

class FeaturedProductsCarouselNosto extends LitElement {
  static properties = {
    loading: {
      reflect: true,
    },
    title: {
      type: String,
    },
    products: {
      converter: {
        fromAttribute: (value, type) => {
          return JSON.parse(decodeURIComponent(value))
        },
      },
    },
    searchResults: {
      attribute: 'search-results',
      type: Boolean,
    },
    resultId: {
      attribute: 'result-id',
      type: String,
    },
  }

  constructor() {
    super()

    // Set default states
    this.title = ''
    this.products = []
    this.searchResults = false
    this.resultId = ''

    this._generateSimpleSlider = this._generateSimpleSlider.bind(this)
    this._generateTitleSection = this._generateTitleSection.bind(this)
    this._generateList = this._generateList.bind(this)
    this._generateProductCard = this._generateProductCard.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    if (typeof this.products === 'string') {
      // In case the converter fails
      this.products = JSON.parse(decodeURIComponent(this.products))
    }
  }

  _generateSimpleSlider() {
    const titleContents = this._generateTitleSection()
    const listContents = this._generateList()

    return html`
      <simple-slider
        class="featured-products-carousel__slider"
        data-nav-a11y="true"
      >
        <div role="region" aria-label="${this.title} Carousel">
          ${titleContents} ${listContents}
        </div>
      </simple-slider>
    `
  }

  _generateList() {
    return html`
      <div class="product-carousel__wrapper">
        <ul
          class="flex container list-unstyled product-carousel__slider hide-scrollbars"
          data-slider-items
        >
          ${repeat(
            this.products,
            (product) => product.id,
            (product, index) => this._generateProductCard(product, index)
          )}
        </ul>
      </div>
    `
  }

  _generateTitleSection() {
    let titleMarkup = html`
      <div class="product-carousel__title-container container">
        ${unsafeHTML(generateDividerMarkup(this.title))}

        <div class="product-carousel__title-row flex jcb aic">
          <p class="visually-hidden">
            This is a ${this.title} carousel. Use Next and Previous buttons to
            navigate.
          </p>
          <h2 class="no-margin product-carousel__title">${this.title}</h2>
          ${this.products.length > 3
            ? html`<div class="slider-nav">
                <ul class="list-unstyled flex">
                  <li>
                    <button
                      class="no-styles arrow--prev"
                      type="button"
                      data-slider-back=""
                      tabindex="0"
                      aria-label="Previous slide"
                    >
                      <span> </span>
                      <span class="icon">
                        <svg
                          width="14"
                          height="13"
                          viewBox="0 0 14 13"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M12.2109 7.336L11.7708 7.77603L7.26303 12.2838L5.76042 10.7812L9.18957 7.35209L0.125667 7.40036L0.125667 6.27341L0.125667 5.14648L9.18967 5.19487L5.76042 1.76563L7.26302 0.263026L11.7708 4.77083L13.2734 6.27343L13.2734 6.27343L12.2109 7.33599L12.2109 7.336L12.2109 7.336Z"
                            fill="black"
                          ></path>
                        </svg>
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      class="no-styles arrow--next"
                      type="button"
                      data-slider-next=""
                      tabindex="0"
                      aria-label="Next Slide"
                    >
                      <span> </span>
                      <span class="icon">
                        <svg
                          width="14"
                          height="13"
                          viewBox="0 0 14 13"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M12.2109 7.336L11.7708 7.77603L7.26303 12.2838L5.76042 10.7812L9.18957 7.35209L0.125667 7.40036L0.125667 6.27341L0.125667 5.14648L9.18967 5.19487L5.76042 1.76563L7.26302 0.263026L11.7708 4.77083L13.2734 6.27343L13.2734 6.27343L12.2109 7.33599L12.2109 7.336L12.2109 7.336Z"
                            fill="black"
                          ></path>
                        </svg>
                      </span>
                    </button>
                  </li>
                </ul>
              </div>`
            : ''}
        </div>
      </div>
    `
    return titleMarkup
  }

  _renderNostoProductCard(item) {
    return html` <product-card
      class="product-card"
      data-url="${item.url}"
      data-nosto-element="product"
      hydrate
      product-id="${item.productId}"
      product-handle="${item.url.split('/').slice(-1)}"
      url-postfix=""
      data-product-href="${item.url}"
      data-product-image-primary="${item.imageUrl}"
      data-product-image-secondary="${item.imageUrl}"
      data-product-title="${item.name}"
      data-product-description=""
      data-product-price="${item.price}"
      data-section-headline=""
      variantType="color"
      is-search-product
      role="group"
      aria-label="item"
    ></product-card>`
  }

  _renderNativeProductCard(item) {
    return html`<product-card
      class="product-card"
      hydrate
      product-id="${item.product_id}"
      product-handle="${item.url.split('/').slice(-1)}"
      url-postfix=""
      data-product-href="${item.url}"
      data-product-image-primary="${item.image_url}"
      data-product-image-secondary="${item.alternate_image_urls[0] || ''}"
      data-product-title="${item.name}"
      data-product-description=""
      data-product-price="${item.price}"
      data-section-headline="${this.sectionHeadline}"
      variantType="color"
      role="group"
      aria-label="item"
    ></product-card>`
  }

  _generateProductCard(item, key) {
    let index = key + 1
    let totalProductSlides = this.products.length

    return html`
      <li data-slider-item>
        ${this.searchResults
          ? this._renderNostoProductCard(item)
          : this._renderNativeProductCard(item)}
      </li>
    `
  }

  createRenderRoot() {
    return this
  }

  render() {
    if (typeof this.products === 'string') {
      this.products = JSON.parse(decodeURIComponent(this.products))
      this.requestUpdate()
    }

    return html`<section class="product-carousel">
      ${this._generateSimpleSlider()}
    </section>`
  }

  static styles = [visuallyHidden, css``]
}

customElements.define(
  'featured-products-carousel-nosto',
  FeaturedProductsCarouselNosto
)
